/**
 * Validates and normalizes video embed URLs.
 *
 * Security: only YouTube and Vimeo are allowed. URLs are normalized to their
 * embed-safe canonical form before being written to the iframe src. The rendered
 * iframe uses sandbox="allow-scripts allow-same-origin" — no top-navigation,
 * no forms, no popups. See project-decisions.md (Video embeds — security).
 *
 * Uses URL() parsing to enforce exact hostname matching — substring regex patterns
 * would allow bypass via domains like "evil-youtube.com".
 */

export interface EmbedResult {
  embedUrl: string;
  provider: 'youtube' | 'vimeo';
}

/** Returns a normalized embed URL or null if the URL is not an allowed provider. */
export function resolveEmbedUrl(url: string): EmbedResult | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const host = parsed.hostname.replace(/^www\./, '');

  if (host === 'youtube.com') {
    const watchId = parsed.searchParams.get('v');
    if (watchId && /^[\w-]{11}$/.test(watchId)) {
      return { embedUrl: `https://www.youtube.com/embed/${watchId}`, provider: 'youtube' };
    }
    const embedMatch = parsed.pathname.match(/^\/embed\/([\w-]{11})$/);
    if (embedMatch) {
      return { embedUrl: `https://www.youtube.com/embed/${embedMatch[1]}`, provider: 'youtube' };
    }
  }

  if (host === 'youtu.be') {
    const id = parsed.pathname.slice(1).split('/')[0];
    if (/^[\w-]{11}$/.test(id)) {
      return { embedUrl: `https://www.youtube.com/embed/${id}`, provider: 'youtube' };
    }
  }

  if (host === 'vimeo.com') {
    const match = parsed.pathname.match(/^\/(?:video\/)?(\d+)/);
    if (match) {
      return { embedUrl: `https://player.vimeo.com/video/${match[1]}`, provider: 'vimeo' };
    }
  }

  return null;
}

export function isAllowedEmbedUrl(url: string): boolean {
  return resolveEmbedUrl(url) !== null;
}
